import React, { useMemo, useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Car, ChevronRight, Ban, Plus } from "lucide-react";
import { formatDateFR, api } from "../../lib/api";
import { TypeIcon, typeColorHex } from "../fleet/typeIcons";
import { useHoverCard, HoverShell, BookingHoverCard, VehicleHoverCard, BOOKING_STATUS } from "./HoverCards";

const FR_DAYS_SHORT = ["D", "L", "M", "M", "J", "V", "S"];
const FR_DAYS_LONG = ["Dimanche","Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi"];
const FR_MONTHS_SHORT = ["Janv","Févr","Mars","Avr","Mai","Juin","Juil","Août","Sept","Oct","Nov","Déc"];

const COL_MIN = 48;
const ROW_H = 48;
const CAT_H = 36;
const HEAD_H = 44;

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate()+n); return r; };
const startOfWeek = (d) => { const r = new Date(d); const off = (r.getDay()+6)%7; r.setDate(r.getDate()-off); return r; };
const daysInMonth = (d) => new Date(d.getFullYear(), d.getMonth()+1, 0).getDate();
const diffDays = (a, b) => Math.round((b - a) / 86400000);
const shiftDateStr = (s, n) => ymd(addDays(new Date(s + "T00:00:00"), n));

const DRAGGABLE_STATUSES = ["confirmed", "pending"];

const MAINT_TYPE_LABELS = {
  entretien: "Entretien courant", vidange: "Vidange", freins: "Freins", pneus: "Pneus",
  controle_technique: "Contrôle technique", reparation: "Réparation mécanique",
  carrosserie: "Carrosserie", nettoyage: "Nettoyage", depannage: "Dépannage", autre: "Autre",
};
const MAINT_STATUS_LABELS = { to_plan: "À planifier", planned: "Planifiée", in_progress: "En cours", done: "Terminée", cancelled: "Annulée" };

/**
 * Calendrier du Pilotage — mois/semaine/jour.
 * Timeline par véhicule avec drag & drop (déplacer / redimensionner les réservations).
 */
export default function PilotageCalendar({
  view, cursor, vehicles, bookings, blocks = [],
  onQuickCreateBooking, onBookingStatusChange, onBookingDatesChange,
  onBlockCreate, onBlockDelete,
}) {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState({});
  const [cellPopover, setCellPopover] = useState(null); // { vehicleId, col, x, y, blockForm? }
  const { hover, cardRef, openHover, scheduleHoverClose, cancelHoverClose, closeHover } = useHoverCard();
  const scrollRef = useRef(null);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [fleetCategories, setFleetCategories] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");
  useEffect(() => {
    api.get("/vendor/settings").then((r) => {
      setVehicleTypes(r.data?.fleet?.vehicle_types || []);
      setFleetCategories(r.data?.fleet?.categories || []);
    }).catch(() => {});
  }, []);
  // Source de vérité : libellé d'une catégorie depuis son id stable (fallback = valeur brute)
  const catLabel = useMemo(() => {
    const m = Object.fromEntries((fleetCategories || []).map((c) => [c.value, c.label]));
    return (val) => m[val] || val;
  }, [fleetCategories]);
  // Types réellement présents dans la flotte (on n'affiche un filtre que s'il y en a ≥ 2)
  const presentTypeValues = useMemo(() => new Set(vehicles.map((v) => v.vehicle_type || "car")), [vehicles]);
  const typeChips = useMemo(() => vehicleTypes.filter((t) => presentTypeValues.has(t.value)), [vehicleTypes, presentTypeValues]);
  const visibleVehicles = useMemo(
    () => (typeFilter === "all" ? vehicles : vehicles.filter((v) => (v.vehicle_type || "car") === typeFilter)),
    [vehicles, typeFilter]
  );

  const days = useMemo(() => {
    if (view === "day") return [new Date(cursor)];
    if (view === "week") {
      const s = startOfWeek(cursor);
      return Array.from({ length: 7 }, (_, i) => addDays(s, i));
    }
    if (view === "quarter") {
      const s = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
      const e = new Date(cursor.getFullYear(), cursor.getMonth() + 3, 0);
      const n = diffDays(s, e) + 1;
      return Array.from({ length: n }, (_, i) => addDays(s, i));
    }
    const total = daysInMonth(cursor);
    return Array.from({ length: total }, (_, i) => new Date(cursor.getFullYear(), cursor.getMonth(), i + 1));
  }, [view, cursor]);

  const totalDays = days.length;
  const colMin = view === "quarter" ? 28 : COL_MIN;
  const rangeStart = days[0];
  const rangeEnd = days[days.length - 1];
  const today = ymd(new Date());

  const grouped = useMemo(() => {
    const map = {};
    visibleVehicles.forEach((v) => {
      const cat = v.category || "Autre";
      if (!map[cat]) map[cat] = [];
      map[cat].push(v);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [visibleVehicles]);

  const vehiclesById = useMemo(() => Object.fromEntries(visibleVehicles.map((v) => [v.id, v])), [visibleVehicles]);

  const bookingsByVehicle = useMemo(() => {
    const map = {};
    bookings.forEach((b) => { if (!map[b.vehicle_id]) map[b.vehicle_id] = []; map[b.vehicle_id].push(b); });
    return map;
  }, [bookings]);

  const blocksByVehicle = useMemo(() => {
    const map = {};
    blocks.forEach((bl) => { if (!map[bl.vehicle_id]) map[bl.vehicle_id] = []; map[bl.vehicle_id].push(bl); });
    return map;
  }, [blocks]);

  const blockBarsForVehicle = (vehicleId) => {
    const vBlocks = blocksByVehicle[vehicleId] || [];
    return vBlocks.map((bl) => {
      const bStart = new Date(bl.start_date + "T00:00:00");
      const bEnd = new Date(bl.end_date + "T00:00:00");
      const visStart = bStart < rangeStart ? rangeStart : bStart;
      const visEnd = bEnd > rangeEnd ? rangeEnd : bEnd;
      if (visStart > rangeEnd || visEnd < rangeStart) return null;
      const startCol = Math.max(0, diffDays(rangeStart, visStart));
      const span = Math.min(diffDays(visStart, visEnd) + 1, totalDays - startCol);
      return { ...bl, startCol, span };
    }).filter(Boolean);
  };

  const barsForVehicle = (vehicleId) => {
    const vBookings = bookingsByVehicle[vehicleId] || [];
    return vBookings.map((b) => {
      const bStart = new Date(b.start_date + "T00:00:00");
      const bEnd = new Date(b.end_date + "T00:00:00");
      const visStart = bStart < rangeStart ? rangeStart : bStart;
      const visEnd = bEnd > rangeEnd ? rangeEnd : bEnd;
      if (visStart > rangeEnd || visEnd < rangeStart) return null;
      const startCol = diffDays(rangeStart, visStart);
      const span = diffDays(visStart, visEnd) + 1;
      return { ...b, startCol: Math.max(0, startCol), span: Math.min(span, totalDays - Math.max(0, startCol)) };
    }).filter(Boolean);
  };

  const toggleCat = (cat) => setCollapsed((p) => ({ ...p, [cat]: !p[cat] }));

  const handleCellClick = (vehicleId, col, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setCellPopover({ vehicleId, col, x: rect.left + rect.width / 2, y: rect.bottom });
  };

  const closePopover = () => setCellPopover(null);

  // ─── Drag & drop des barres de réservation ──────
  const [drag, setDrag] = useState(null); // { id, mode, startX, colW, delta, booking }
  const dragRef = useRef(null);
  dragRef.current = drag;
  const dragMovedRef = useRef(false);

  const startDrag = (b, mode, e) => {
    if (!DRAGGABLE_STATUSES.includes(b.status)) return;
    e.preventDefault();
    e.stopPropagation();
    const grid = e.currentTarget.closest("[data-grid]") || e.currentTarget.parentElement;
    const colW = grid ? grid.getBoundingClientRect().width / totalDays : colMin;
    dragMovedRef.current = false;
    closeHover();
    setDrag({ id: b.id, mode, startX: e.clientX, colW, delta: 0, booking: b });
  };

  useEffect(() => {
    if (!drag) return;
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d) return;
      if (Math.abs(e.clientX - d.startX) > 4) dragMovedRef.current = true;
      const delta = Math.round((e.clientX - d.startX) / d.colW);
      if (delta !== d.delta) setDrag({ ...d, delta });
    };
    const onUp = () => {
      const d = dragRef.current;
      setDrag(null);
      setTimeout(() => { dragMovedRef.current = false; }, 60);
      if (!d || d.delta === 0) return;
      let sd = d.booking.start_date;
      let ed = d.booking.end_date;
      if (d.mode === "move") { sd = shiftDateStr(sd, d.delta); ed = shiftDateStr(ed, d.delta); }
      else if (d.mode === "resize-r") { ed = shiftDateStr(ed, d.delta); if (ed < sd) ed = sd; }
      else if (d.mode === "resize-l") { sd = shiftDateStr(sd, d.delta); if (sd > ed) sd = ed; }
      if (sd !== d.booking.start_date || ed !== d.booking.end_date) {
        onBookingDatesChange?.(d.booking.id, sd, ed);
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!drag]);

  const gridW = totalDays * colMin;

  return (
    <div className="h-full overflow-hidden p-4 sm:p-6">
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden h-full flex flex-col max-w-full" data-testid="pilotage-calendar">
        {/* Legend */}
        <div className="shrink-0 px-4 py-2 border-b border-slate-100 bg-slate-50/50 flex flex-wrap gap-3 text-[10px] text-slate-500">
          {Object.entries(BOOKING_STATUS).map(([k, s]) => (
            <div key={k} className="flex items-center gap-1"><span className={`h-2 w-2 rounded-sm ${s.bg}`} />{s.label}</div>
          ))}
        </div>

        {/* Filtre par Type de bien (affiché seulement si ≥ 2 types présents) */}
        {typeChips.length >= 2 && (
          typeChips.length > 8 ? (
            <div className="shrink-0 px-4 py-2 border-b border-slate-100 flex items-center gap-2" data-testid="calendar-type-filter">
              <span className="text-[11px] text-slate-500 shrink-0">Type</span>
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="h-8 px-2 rounded-lg border border-slate-200 bg-white text-xs" data-testid="calendar-type-select">
                <option value="all">Tous les types</option>
                {typeChips.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          ) : (
            <div className="shrink-0 px-4 py-2 border-b border-slate-100 flex items-center gap-1.5 overflow-x-auto" data-testid="calendar-type-filter">
              <button type="button" onClick={() => setTypeFilter("all")}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border shrink-0 ${typeFilter === "all" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
                data-testid="calendar-type-chip-all">Tous</button>
              {typeChips.map((t) => (
                <button key={t.value} type="button" onClick={() => setTypeFilter(t.value)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border shrink-0 ${typeFilter === t.value ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
                  data-testid={`calendar-type-chip-${t.value}`}>
                  <TypeIcon name={t.icon} className="h-3 w-3" style={typeFilter === t.value ? undefined : { color: typeColorHex(t.color) }} />
                  {t.label}
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: typeColorHex(t.color) }} />
                </button>
              ))}
            </div>
          )
        )}

        <div className="flex-1 min-h-0 flex overflow-hidden">
          {/* LEFT — sticky vehicle column */}
          <div className="w-36 sm:w-48 shrink-0 border-r border-slate-200 bg-white overflow-y-auto" data-testid="pilotage-left-panel">
            <div className="border-b border-slate-200 bg-slate-50 px-3 flex items-center sticky top-0 z-10" style={{ height: HEAD_H }}>
              <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400">Véhicules</span>
            </div>
            {grouped.map(([cat, vehs]) => (
              <React.Fragment key={cat}>
                <button
                  onClick={() => toggleCat(cat)}
                  className="w-full flex items-center gap-2 px-3 bg-slate-50 border-b border-slate-200 hover:bg-slate-100 transition-colors text-left"
                  style={{ height: CAT_H }}
                >
                  <ChevronRight className={`h-3 w-3 text-slate-400 transition-transform ${collapsed[cat] ? "" : "rotate-90"}`} />
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 truncate">{catLabel(cat)}</span>
                  <span className="text-[9px] text-slate-400">({vehs.length})</span>
                </button>
                {!collapsed[cat] && vehs.map((v) => (
                  <div
                    key={v.id}
                    className="flex items-center gap-2 px-3 border-b border-slate-100 bg-white hover:bg-violet-50/40 transition-colors cursor-default"
                    style={{ height: ROW_H }}
                    onMouseEnter={(e) => openHover("vehicle", v, e)}
                    onMouseLeave={scheduleHoverClose}
                    data-testid={`calendar-vehicle-label-${v.id}`}
                  >
                    <div className="h-6 w-6 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                      <Car className="h-3 w-3 text-slate-400" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[11px] font-medium text-slate-900 truncate">{(v.internal_ref || "").trim() || `${v.brand || ""} ${v.model || ""}`.trim() || "—"}</div>
                      <div className="text-[9px] text-slate-400 truncate">{`${v.brand || ""} ${v.model || ""}`.trim() || "—"}</div>
                    </div>
                  </div>
                ))}
              </React.Fragment>
            ))}
            {vehicles.length === 0 && (
              <div className="p-4 text-xs text-slate-400">Aucun véhicule</div>
            )}
          </div>

          {/* RIGHT — scrollable timeline */}
          <div className="flex-1 min-w-0 overflow-auto" ref={scrollRef} data-testid="pilotage-scroll">
            <div style={{ minWidth: view === "day" ? "100%" : gridW }}>
              {/* Day headers */}
              <div
                className="grid border-b border-slate-200 bg-slate-50 sticky top-0 z-10"
                style={{ gridTemplateColumns: `repeat(${totalDays}, minmax(${colMin}px, 1fr))`, height: HEAD_H }}
              >
                {days.map((d) => {
                  const isToday = ymd(d) === today;
                  const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                  const isMonthStart = view === "quarter" && d.getDate() === 1;
                  return (
                    <div key={ymd(d)} className={`flex flex-col items-center justify-center border-r last:border-r-0 ${isMonthStart ? "border-slate-300" : "border-slate-100"} ${isWeekend ? "bg-slate-100/50" : ""}`}>
                      <div className={`text-[9px] font-medium leading-none ${isMonthStart ? "text-violet-600 font-bold" : "text-slate-400"}`}>
                        {view === "day" ? FR_DAYS_LONG[d.getDay()] : isMonthStart ? FR_MONTHS_SHORT[d.getMonth()] : FR_DAYS_SHORT[d.getDay()]}
                      </div>
                      <div className={`text-[11px] font-bold leading-none mt-0.5 ${isToday ? "text-violet-600" : "text-slate-700"}`}>{d.getDate()}</div>
                    </div>
                  );
                })}
              </div>

              {/* Vehicle rows */}
              {grouped.map(([cat, vehs]) => (
                <React.Fragment key={cat}>
                  <div className="border-b border-slate-200 bg-slate-50" style={{ height: CAT_H }} />
                  {!collapsed[cat] && vehs.map((v) => (
                    <TimelineRow
                      key={v.id}
                      days={days}
                      totalDays={totalDays}
                      colMin={colMin}
                      today={today}
                      bars={barsForVehicle(v.id)}
                      blockBars={blockBarsForVehicle(v.id)}
                      onBlockClick={(bl) => {
                        if (bl.maintenance_id) {
                          navigate(`/vendor/pilotage?tab=vehicles&vehicle=${bl.vehicle_id}&vtab=maintenance`);
                          return;
                        }
                        if (window.confirm(`Débloquer la période du ${formatDateFR(bl.start_date)} au ${formatDateFR(bl.end_date)}${bl.reason ? ` (${bl.reason})` : ""} ?`)) {
                          onBlockDelete?.(bl.id);
                        }
                      }}
                      vehicleId={v.id}
                      drag={drag}
                      onCellClick={handleCellClick}
                      onBookingClick={(b) => {
                        if (dragMovedRef.current) return;
                        navigate(`/vendor/pilotage?tab=bookings&booking=${b.id}`);
                      }}
                      onBarPointerDown={startDrag}
                      onItemHover={(b, e) => { if (!drag) openHover("booking", b, e, vehiclesById[b.vehicle_id]); }}
                      onItemLeave={scheduleHoverClose}
                    />
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Cell creation popover */}
      {cellPopover && (
        <>
          <div className="fixed inset-0 z-40" onClick={closePopover} />
          <div
            className={`fixed z-50 bg-white border border-slate-200 rounded-xl shadow-xl p-1 flex flex-col ${cellPopover.blockForm ? "w-64" : "w-48"}`}
            style={{
              left: Math.min(Math.max(cellPopover.x - 96, 8), window.innerWidth - (cellPopover.blockForm ? 270 : 200)),
              top: cellPopover.y + 4,
            }}
            data-testid="pilotage-cell-popover"
          >
            <div className="px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400 border-b border-slate-100">
              {cellPopover.blockForm ? "Bloquer à partir du" : "Créer sur"} {formatDateFR(ymd(days[cellPopover.col]))}
            </div>
            {cellPopover.blockForm ? (
              <BlockForm
                startDate={ymd(days[cellPopover.col])}
                onSubmit={(payload) => {
                  onBlockCreate?.(cellPopover.vehicleId, payload);
                  closePopover();
                }}
                onCancel={closePopover}
              />
            ) : (
              <>
                <button
                  onClick={() => {
                    const date = ymd(days[cellPopover.col]);
                    if (onQuickCreateBooking) onQuickCreateBooking({ vehicle_id: cellPopover.vehicleId || "", date });
                    closePopover();
                  }}
                  className="flex items-center gap-2 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 rounded-lg"
                  data-testid="popover-create-booking"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Réservation
                </button>
                {cellPopover.vehicleId && (
                  <button
                    onClick={() => setCellPopover({ ...cellPopover, blockForm: true })}
                    className="flex items-center gap-2 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 rounded-lg"
                    data-testid="popover-block-period"
                  >
                    <Ban className="h-3.5 w-3.5 text-slate-400" /> Bloquer la période
                  </button>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Hover cards */}
      {hover?.type === "booking" && (
        <HoverShell x={hover.x} y={hover.y} cardRef={cardRef} onMouseEnter={cancelHoverClose} onMouseLeave={scheduleHoverClose}>
          <BookingHoverCard booking={hover.item} vehicle={hover.extra} onStatusChange={onBookingStatusChange} onClose={closeHover} />
        </HoverShell>
      )}
      {hover?.type === "vehicle" && (
        <HoverShell x={hover.x} y={hover.y} cardRef={cardRef} onMouseEnter={cancelHoverClose} onMouseLeave={scheduleHoverClose}>
          <VehicleHoverCard vehicle={hover.item} onClose={closeHover} catLabel={catLabel} />
        </HoverShell>
      )}
    </div>
  );
}

function BlockForm({ startDate, onSubmit, onCancel }) {
  const [end, setEnd] = useState(startDate);
  const [reason, setReason] = useState("");
  return (
    <div className="p-2 space-y-2" data-testid="block-form">
      <div>
        <label className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500">Jusqu'au (inclus)</label>
        <input
          type="date"
          value={end}
          min={startDate}
          onChange={(e) => setEnd(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs focus:ring-1 focus:ring-violet-500 outline-none"
          data-testid="block-end-date"
        />
      </div>
      <div>
        <label className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500">Motif (optionnel)</label>
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Entretien, usage perso…"
          className="mt-1 w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs focus:ring-1 focus:ring-violet-500 outline-none"
          data-testid="block-reason"
        />
      </div>
      <div className="flex gap-1.5">
        <button onClick={onCancel} className="flex-1 h-8 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50" data-testid="block-cancel">Annuler</button>
        <button
          onClick={() => onSubmit({ start_date: startDate, end_date: end || startDate, reason })}
          className="flex-1 h-8 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800"
          data-testid="block-submit"
        >
          Bloquer
        </button>
      </div>
    </div>
  );
}

function TimelineRow({
  days, totalDays, colMin = COL_MIN, today, bars, blockBars = [], vehicleId, drag,
  onCellClick, onBookingClick, onBarPointerDown, onItemHover, onItemLeave, onBlockClick,
}) {
  return (
    <div
      className="relative border-b border-slate-100 hover:bg-violet-50/10 transition-colors"
      style={{ height: ROW_H }}
      data-testid={`pilotage-row-${vehicleId || "unassigned"}`}
    >
      {/* Background grid — click-to-create */}
      <div className="absolute inset-0 grid" style={{ gridTemplateColumns: `repeat(${totalDays}, minmax(${colMin}px, 1fr))` }}>
        {days.map((d, i) => {
          const isWeekend = d.getDay() === 0 || d.getDay() === 6;
          const isToday = ymd(d) === today;
          return (
            <button
              type="button"
              key={ymd(d)}
              onClick={(e) => onCellClick(vehicleId, i, e)}
              title="Créer une réservation"
              className={`group/cell relative border-r border-slate-100 last:border-r-0 transition-colors ${
                isWeekend ? "bg-slate-50/60" : ""
              } ${isToday ? "bg-violet-50/40" : ""}`}
              data-testid={`pilotage-cell-${vehicleId || "none"}-${i}`}
            >
              <span
                className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 group-hover/cell:opacity-100 transition-opacity"
                data-testid={`pilotage-cell-hint-${vehicleId || "none"}-${i}`}
              >
                <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-violet-600 text-white shadow-sm">
                  <Plus className="h-3 w-3" strokeWidth={3} />
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {/* Booking bars */}
      <div className="absolute inset-0 grid items-center pointer-events-none" style={{ gridTemplateColumns: `repeat(${totalDays}, minmax(${colMin}px, 1fr))` }} data-grid>
        {blockBars.map((bl) => {
          const isMaint = !!bl.maintenance_id;
          const maintTitle = isMaint
            ? `🔧 Intervention ${bl.maintenance_ref || ""} · ${MAINT_TYPE_LABELS[bl.maintenance_type] || bl.maintenance_type || ""}${bl.provider ? ` · ${bl.provider}` : ""} · ${formatDateFR(bl.start_date)} → ${formatDateFR(bl.end_date)} · ${MAINT_STATUS_LABELS[bl.maintenance_status] || bl.maintenance_status || ""} (cliquer pour ouvrir la fiche)`
            : `Bloqué ${formatDateFR(bl.start_date)} → ${formatDateFR(bl.end_date)}${bl.reason ? ` · ${bl.reason}` : ""} (cliquer pour débloquer)`;
          return (
          <button
            key={`block-${bl.id}`}
            type="button"
            onClick={(e) => { e.stopPropagation(); onBlockClick?.(bl); }}
            className={`relative text-[9px] font-semibold rounded-md px-1.5 py-0.5 truncate transition-colors pointer-events-auto cursor-pointer ${
              isMaint ? "bg-amber-300/90 text-amber-900 hover:bg-amber-400/90" : "bg-slate-300/80 text-slate-600 hover:bg-slate-400/80"
            }`}
            style={{
              gridColumn: `${bl.startCol + 1} / span ${bl.span}`,
              margin: "6px 1px",
              backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(255,255,255,0.45) 4px, rgba(255,255,255,0.45) 8px)",
            }}
            title={maintTitle}
            data-testid={`pilotage-block-${bl.id}`}
          >
            {isMaint ? `🔧 ${bl.maintenance_ref || "Garage"}` : `⛔ ${bl.reason || "Bloqué"}`}
          </button>
          );
        })}
        {bars.map((b) => {
          const st = BOOKING_STATUS[b.status] || BOOKING_STATUS.confirmed;
          const draggable = DRAGGABLE_STATUSES.includes(b.status);
          let sc = b.startCol;
          let sp = b.span;
          const isDragging = drag && drag.id === b.id;
          if (isDragging && drag.delta) {
            if (drag.mode === "move") sc = Math.max(0, Math.min(totalDays - sp, sc + drag.delta));
            else if (drag.mode === "resize-r") sp = Math.max(1, Math.min(totalDays - sc, sp + drag.delta));
            else if (drag.mode === "resize-l") {
              const nsc = Math.max(0, Math.min(b.startCol + b.span - 1, b.startCol + drag.delta));
              sp = b.startCol + b.span - nsc;
              sc = nsc;
            }
          }
          return (
            <button
              key={b.id}
              type="button"
              onClick={(e) => { e.stopPropagation(); onBookingClick(b); }}
              onPointerDown={draggable ? (e) => onBarPointerDown(b, "move", e) : undefined}
              onMouseEnter={(e) => onItemHover?.(b, e)}
              onMouseLeave={onItemLeave}
              className={`relative ${st.bg} text-white text-[9px] font-semibold rounded-md px-1.5 py-0.5 truncate hover:brightness-110 hover:shadow-md transition-all pointer-events-auto ${
                draggable ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"
              } ${isDragging ? "ring-2 ring-violet-400 shadow-lg z-10 opacity-90" : ""}`}
              style={{ gridColumn: `${sc + 1} / span ${sp}`, margin: "6px 1px", touchAction: "none" }}
              title={`${b.reference ? `${b.reference} · ` : ""}${b.client_name} · ${formatDateFR(b.start_date)}${b.start_time ? ` ${b.start_time}` : ""} → ${formatDateFR(b.end_date)}${b.end_time ? ` ${b.end_time}` : ""}`}
              data-testid={`pilotage-booking-${b.id}`}
            >
              {b.client_name?.split(" ")[0]}
              {draggable && (
                <>
                  <span
                    onPointerDown={(e) => onBarPointerDown(b, "resize-l", e)}
                    className="absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize rounded-l-md hover:bg-white/30"
                    data-testid={`pilotage-booking-resize-l-${b.id}`}
                  />
                  <span
                    onPointerDown={(e) => onBarPointerDown(b, "resize-r", e)}
                    className="absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize rounded-r-md hover:bg-white/30"
                    data-testid={`pilotage-booking-resize-r-${b.id}`}
                  />
                </>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
